import { ArrowDown, ArrowUp, ArrowUpDown, Link2, Mail } from "lucide-react";
import { toast } from "sonner";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";

export const STATUS_LABEL = { active: "Active", invited: "Invited", archived: "Suspended" };
const STATUS_TONE = { active: "bg-green-50 text-green-700 border-green-200", invited: "bg-sky-50 text-sky-700 border-sky-200", archived: "bg-rose-50 text-rose-700 border-rose-200" };

export const relTime = (iso) => {
  if (!iso) return "Never";
  const d = (Date.now() - new Date(iso).getTime()) / 86400000;
  if (d < 1 / 24) return "Just now";
  if (d < 1) return `${Math.round(d * 24)}h ago`;
  if (d < 30) return `${Math.round(d)}d ago`;
  return new Date(iso).toLocaleDateString(undefined, { day: "numeric", month: "short", year: "numeric" });
};

export const copyInvite = async (l) => {
  try { await navigator.clipboard.writeText(l.invite_url); toast.success(`Invite link for ${l.name} copied — paste it into WhatsApp, a text or your own email`); }
  catch { window.prompt(`Copy ${l.name}'s invite link:`, l.invite_url); }
};

export const sortRows = (rows, { key, dir }, pctKey = "progress_pct") => {
  const k = key === "progress" ? pctKey : key;
  return [...rows].sort((a, b) => {
    const x = a[k] ?? ""; const y = b[k] ?? "";
    const c = typeof x === "number" ? x - y : String(x).localeCompare(String(y), undefined, { sensitivity: "base" });
    return dir === "asc" ? c : -c;
  });
};

const Th = ({ label, k, sort, onSort, className = "" }) => {
  const Icon = sort.key !== k ? ArrowUpDown : sort.dir === "asc" ? ArrowUp : ArrowDown;
  return (
    <th className={`px-3 py-2.5 font-medium ${className}`}>
      <button onClick={() => onSort(k)} className="inline-flex items-center gap-1 hover:text-slate-900" data-testid={`sort-${k}`}>{label} <Icon size={11} className={sort.key === k ? "text-green-700" : "text-slate-300"} /></button>
    </th>
  );
};

const ProgressCell = ({ pct, sub }) => (
  <div className="w-36">
    <div className="flex items-center justify-between text-[11px]"><span className="font-semibold text-slate-800">{pct}%</span><span className="text-slate-400">{sub}</span></div>
    <div className="mt-1 h-1.5 overflow-hidden rounded-full bg-slate-100"><div className={`h-full rounded-full ${pct === 100 ? "bg-green-600" : "bg-green-500/80"}`} style={{ width: `${pct}%` }} /></div>
  </div>
);

export const LearnerTable = ({ rows, selected, onToggle, onToggleAll, sort, onSort, onOpen, onManage, pctKey = "progress_pct", showCourses = true }) => {
  const allOn = rows.length > 0 && rows.every((r) => selected.has(r.id));
  return (
    <div className="overflow-x-auto rounded-xl border border-border bg-white">
      <table className="w-full text-sm" data-testid="learner-table">
        <thead className="bg-slate-50 text-left text-xs text-slate-500">
          <tr>
            <th className="w-10 px-3 py-2.5"><input type="checkbox" checked={allOn} onChange={() => onToggleAll(!allOn)} data-testid="select-all-learners" /></th>
            <Th label="Learner" k="name" sort={sort} onSort={onSort} />
            <Th label="Status" k="status" sort={sort} onSort={onSort} />
            {showCourses && <th className="px-3 py-2.5 font-medium">Courses</th>}
            <Th label="Progress" k="progress" sort={sort} onSort={onSort} />
            <Th label="Last active" k="last_active_at" sort={sort} onSort={onSort} />
            <Th label="Invited" k="invited_at" sort={sort} onSort={onSort} className="hidden lg:table-cell" />
            {onManage && <th className="px-3 py-2.5" />}
          </tr>
        </thead>
        <tbody>
          {rows.map((l) => (
            <tr key={l.id} onClick={() => onOpen(l.id)} className={`cursor-pointer border-t border-border transition-colors hover:bg-green-50/50 ${selected.has(l.id) ? "bg-green-50/70" : ""}`} data-testid={`learner-row-${l.id}`}>
              <td className="px-3 py-2.5" onClick={(e) => e.stopPropagation()}><input type="checkbox" checked={selected.has(l.id)} onChange={() => onToggle(l.id)} data-testid={`select-learner-${l.id}`} /></td>
              <td className="px-3 py-2.5"><p className="flex items-center gap-1.5 font-medium text-slate-900">{l.name}{l.email_opt_in && <span title="Opted in to emails" className="inline-flex items-center gap-0.5 rounded-full bg-green-50 px-1.5 text-[9px] font-semibold text-green-700" data-testid={`learner-opted-in-${l.id}`}><Mail size={9} /> Emails on</span>}</p><p className="text-xs text-muted-foreground">{l.email}</p></td>
              <td className="px-3 py-2.5"><Badge variant="outline" className={`text-[10px] ${STATUS_TONE[l.status] || ""}`}>{STATUS_LABEL[l.status] || l.status}</Badge>
                {l.status === "invited" && l.invite_url && (
                  <button onClick={(e) => { e.stopPropagation(); copyInvite(l); }} className="mt-0.5 flex items-center gap-1 text-[10px] font-medium text-green-700 hover:underline" data-testid={`copy-invite-${l.id}`}><Link2 size={10} /> Copy invite link</button>
                )}
                {l.status === "invited" && l.last_reminded_at && <p className="mt-0.5 text-[10px] text-slate-500" data-testid={`learner-reminded-${l.id}`}>Reminder {l.reminders_sent} sent {relTime(l.last_reminded_at)}</p>}</td>
              {showCourses && <td className="max-w-[220px] px-3 py-2.5 text-xs text-slate-600"><span className="line-clamp-2" title={l.course_titles.join(", ")}>{l.course_titles.join(", ") || <em className="text-slate-400">None</em>}</span></td>}
              <td className="px-3 py-2.5"><ProgressCell pct={l[pctKey] ?? 0} sub={pctKey === "progress_pct" ? `${l.lessons_done}/${l.lessons_total}` : ""} /></td>
              <td className="px-3 py-2.5 text-xs text-slate-600" title={l.last_active_at || ""}>{relTime(l.last_active_at)}</td>
              <td className="hidden px-3 py-2.5 text-xs text-muted-foreground lg:table-cell">{l.invited_at ? new Date(l.invited_at).toLocaleDateString() : "—"}</td>
              {onManage && <td className="px-3 py-2.5 text-right"><Button size="sm" variant="outline" className="h-7 text-xs" onClick={(e) => { e.stopPropagation(); onManage(l); }} data-testid={`learner-manage-${l.id}`}>Manage</Button></td>}
            </tr>
          ))}
          {rows.length === 0 && <tr><td colSpan={8} className="px-3 py-10 text-center text-sm text-muted-foreground" data-testid="learner-table-empty">No learners match.</td></tr>}
        </tbody>
      </table>
    </div>
  );
};
